import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { renderMarkdown } from "../../lib/newsMarkdown";
import { Plus, Save, Trash2, Sparkles, Image as ImageIcon, Eye, Send, RefreshCw, X, Newspaper, Globe, Tv as TvIcon } from "lucide-react";

const CATEGORIES = ["BREAKING NEWS", "TAP IN NEWS", "WEATHER", "MUSIC", "SPORTS", "STREAMING", "CULTURE", "EXCLUSIVE"];

const EMPTY = {
  id: null,
  slug: "",
  title: "",
  dek: "",
  category: "BREAKING NEWS",
  hero_image_url: "",
  body_md: "",
  author_name: "",
  status: "draft",
  tags: [],
};

export default function NewsAdminTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null OR a post object
  const [busy, setBusy] = useState(false);
  const [tagsInput, setTagsInput] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const [showCatcher, setShowCatcher] = useState(false);
  const [catcherBusy, setCatcherBusy] = useState(false);
  const [catcherSources, setCatcherSources] = useState([]); // registry from /sources
  const [catcherWeights, setCatcherWeights] = useState({}); // {sid: number}
  const [catcherKwInclude, setCatcherKwInclude] = useState("");
  const [catcherKwBlock, setCatcherKwBlock] = useState("");
  const [catcherCfg, setCatcherCfg] = useState({
    count: 8,
    rewrite: true,
    publish: false,
    generate_heroes: false,
  });
  const [catcherResult, setCatcherResult] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // Smart Topic Buckets — one-tap presets that auto-set source weights
  // + keywords for common editorial vibes. Each bucket activates only
  // the listed sources (others muted to 0) so the catcher stays focused.
  const TOPIC_BUCKETS = [
    { id: "hiphop",  label: "Hip-Hop Today", emoji: "🎤", sources: ["pitchfork", "complex", "rollingstone", "billboard", "tmz"], keywords: ["rap", "hip-hop", "drill", "kendrick", "drake", "j cole", "beat"] },
    { id: "nba",     label: "NBA Tonight",   emoji: "🏀", sources: ["espn", "bleacher", "yahoo_sports", "tmz_sports"],            keywords: ["nba", "lakers", "warriors", "celtics", "lebron", "curry", "playoffs"] },
    { id: "fashion", label: "Fashion Friday",emoji: "👗", sources: ["vogue", "harpers", "elle"],                                  keywords: ["fashion", "runway", "style", "designer", "couture", "red carpet"] },
    { id: "tech",    label: "Tech Pulse",    emoji: "💻", sources: ["techcrunch", "verge", "wired", "engadget"],                  keywords: ["AI", "startup", "gadget", "app", "iphone", "openai"] },
    { id: "weather", label: "Storm Watch",   emoji: "⛈️", sources: ["weather_nws", "weather_acc", "cnn"],                          keywords: ["storm", "hurricane", "snow", "blizzard", "tornado", "heat", "weather"] },
    { id: "drama",   label: "Late Night Drama", emoji: "🍿", sources: ["tmz", "hollywood", "deadline", "variety", "ew"],            keywords: ["scandal", "drama", "exclusive", "split", "feud"] },
    { id: "food",    label: "What's Cooking",emoji: "🍝", sources: ["buzzfeed", "ew", "complex"],                                 keywords: ["recipe", "restaurant", "food", "cooking", "viral", "tiktok"] },
    { id: "politics",label: "Politics Now",  emoji: "🏛️", sources: ["cnn", "fox", "nbc", "guardian", "nyt", "ap"],                keywords: ["election", "senate", "congress", "white house", "supreme court", "campaign"] },
  ];

  const applyBucket = (bucket) => {
    const newWeights = {};
    catcherSources.forEach((s) => { newWeights[s.id] = bucket.sources.includes(s.id) ? 1.5 : 0; });
    setCatcherWeights(newWeights);
    setCatcherKwInclude(bucket.keywords.join(", "));
    toast.success(`${bucket.emoji} ${bucket.label} preset loaded — tap "Catch top stories now"`);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/news", { params: { include_drafts: true, page_size: 50 } });
      setPosts(r.data?.posts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const startNew = () => {
    setEditing({ ...EMPTY });
    setTagsInput("");
    setShowPreview(false);
  };

  const startEdit = async (p) => {
    try {
      const r = await api.get(`/news/${p.slug}`);
      setEditing(r.data);
      setTagsInput((r.data.tags || []).join(", "));
      setShowPreview(false);
    } catch (e) {
      toast.error("Couldn't load post for editing");
    }
  };

  const cancelEdit = () => { setEditing(null); setShowPreview(false); };

  const handleSave = async ({ publish = false } = {}) => {
    if (!editing.title.trim()) {
      toast.error("Headline is required");
      return;
    }
    setBusy(true);
    const tags = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
    const payload = {
      title: editing.title,
      dek: editing.dek,
      category: editing.category,
      hero_image_url: editing.hero_image_url,
      body_md: editing.body_md,
      author_name: editing.author_name,
      tags,
      status: publish ? "published" : (editing.status || "draft"),
      slug: editing.slug || undefined,
    };
    try {
      let saved;
      if (editing.id) {
        const r = await api.patch(`/news/${editing.id}`, payload);
        saved = r.data;
      } else {
        const r = await api.post("/news", payload);
        saved = r.data;
      }
      toast.success(publish ? "Published & share card generated" : "Saved");
      setEditing(saved);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const handlePublishToggle = async () => {
    if (!editing?.id) {
      toast.error("Save first");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/news/${editing.id}/publish`);
      toast.success(`Status: ${r.data.status}`);
      const fresh = await api.get(`/news/${r.data.slug}`);
      setEditing(fresh.data);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/news/${p.id}`);
      toast.success("Post deleted");
      if (editing?.id === p.id) setEditing(null);
      refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleAiHero = async () => {
    if (!editing?.id) {
      toast.error("Save the post first so we can attach the hero image");
      return;
    }
    setBusy(true);
    toast.info("Generating hero image with Gemini Nano Banana — ~10s...");
    try {
      const r = await api.post(`/news/${editing.id}/hero/ai`, {});
      toast.success("AI hero generated");
      const fresh = await api.get(`/news/${r.data.slug || editing.slug}`);
      setEditing(fresh.data);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI hero failed — try again or upload a hero manually");
    } finally {
      setBusy(false);
    }
  };

  const handleHeroFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Hero image must be under 5 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setEditing((prev) => ({ ...prev, hero_image_url: e.target.result }));
      toast.success("Hero attached — remember to save");
    };
    reader.readAsDataURL(file);
  };

  const handleRegenOg = async () => {
    if (!editing?.id) return;
    setBusy(true);
    try {
      const r = await api.post(`/news/${editing.id}/og`);
      toast.success("Share card regenerated");
      setEditing((prev) => ({ ...prev, og_image_url: r.data.og_image_url }));
    } catch {
      toast.error("Regen failed");
    } finally {
      setBusy(false);
    }
  };

  // Lazy-load the source registry the first time the catcher panel opens.
  const loadCatcherSources = async () => {
    try {
      const r = await api.get("/news/auto-catcher/sources");
      setCatcherSources(r.data?.sources || []);
      const w = {};
      (r.data?.sources || []).forEach((s) => { w[s.id] = s.weight; });
      setCatcherWeights(w);
      setCatcherKwInclude((r.data?.global_keywords_include || []).join(", "));
      setCatcherKwBlock((r.data?.global_keywords_block || []).join(", "));
    } catch (e) {
      toast.error("Couldn't load source registry");
    }
  };

  useEffect(() => {
    if (showCatcher && catcherSources.length === 0) loadCatcherSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCatcher]);

  const handleSaveCatcherSettings = async () => {
    setSavingSettings(true);
    try {
      await api.patch("/news/auto-catcher/settings", {
        weights: catcherWeights,
        keywords_include: catcherKwInclude.split(",").map((x) => x.trim()).filter(Boolean),
        keywords_block: catcherKwBlock.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Source Trust settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRunCatcher = async () => {
    setCatcherBusy(true);
    setCatcherResult(null);
    const enabledSources = Object.keys(catcherWeights).filter((sid) => (catcherWeights[sid] || 0) > 0);
    toast.info(`Catching from ${enabledSources.length} source${enabledSources.length === 1 ? "" : "s"}…${catcherCfg.rewrite ? " (Gemini rewrite ON)" : ""}`);
    try {
      const r = await api.post("/news/auto-catcher/run", {
        count: catcherCfg.count,
        sources: enabledSources,
        weights: catcherWeights,
        keywords: catcherKwInclude.split(",").map((x) => x.trim()).filter(Boolean),
        block_keywords: catcherKwBlock.split(",").map((x) => x.trim()).filter(Boolean),
        rewrite: catcherCfg.rewrite,
        status: catcherCfg.publish ? "published" : "draft",
        generate_heroes: catcherCfg.generate_heroes,
      });
      setCatcherResult(r.data);
      toast.success(`Caught ${r.data.count} stor${r.data.count === 1 ? "y" : "ies"}${catcherCfg.publish ? " — live now" : " — saved as drafts"}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Story Catcher failed");
    } finally {
      setCatcherBusy(false);
    }
  };

  const previewHtml = useMemo(
    () => editing ? renderMarkdown(editing.body_md || "") : "",
    [editing?.body_md]
  );

  return (
    <div data-testid="news-admin-tab" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-display font-black tracking-tight">Tap In News</h2>
          <p className="text-white/60 text-sm">TMZ-style stories, auto-generated share cards, AI hero fallback.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="news-admin-catcher-toggle"
            onClick={() => setShowCatcher((v) => !v)}
            className="inline-flex items-center gap-1.5 bg-fuchsia-500/20 hover:bg-fuchsia-500/30 ring-1 ring-fuchsia-500/40 text-fuchsia-200 px-3 py-2 rounded-lg font-bold text-sm"
          >
            <Globe className="w-4 h-4" /> Story Catcher
          </button>
          <button
            data-testid="news-admin-new-post"
            onClick={startNew}
            className="inline-flex items-center gap-1.5 bg-tv-accent text-black px-4 py-2 rounded-lg font-bold hover:opacity-90"
          >
            <Plus className="w-4 h-4" /> New post
          </button>
        </div>
      </div>

      {showCatcher && !editing && (
        <div data-testid="news-admin-catcher-panel" className="rounded-xl bg-gradient-to-br from-fuchsia-500/10 via-black to-black ring-1 ring-fuchsia-500/30 p-4 sm:p-5 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-display font-black text-lg flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-fuchsia-300" /> Story Catcher
              </h3>
              <p className="text-white/65 text-xs mt-1 max-w-2xl">
                Pulls real headlines from {catcherSources.length || "30+"} curated outlets across <span className="text-fuchsia-300 font-bold">News, Weather, Sports, Music, Entertainment, Fashion, Tech, Business</span>. Use the Source Trust sliders below to weight each outlet (0 = off, 1 = normal, 2 = picked twice as often). Add include/block keywords to zoom in on specific stories.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowCatcher(false)}
              className="text-white/50 hover:text-white"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Run controls — count + global toggles */}
          <div className="grid gap-3 sm:grid-cols-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold block mb-1">How many stories</label>
              <input
                data-testid="news-admin-catcher-count"
                type="number" min={1} max={16}
                value={catcherCfg.count}
                onChange={(e) => setCatcherCfg((p) => ({ ...p, count: Math.max(1, Math.min(16, parseInt(e.target.value) || 1)) }))}
                className="w-full px-3 py-2 bg-black/40 ring-1 ring-white/15 rounded-lg text-white text-sm focus:ring-fuchsia-400 outline-none"
              />
            </div>
            <div className="sm:col-span-3">
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold block mb-1">Run options</label>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-1.5">
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    data-testid="news-admin-catcher-rewrite"
                    checked={catcherCfg.rewrite}
                    onChange={(e) => setCatcherCfg((p) => ({ ...p, rewrite: e.target.checked }))}
                    className="accent-fuchsia-500"
                  />
                  <span className="font-bold text-white/80">Rewrite in Tap In voice (Gemini)</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    data-testid="news-admin-catcher-publish"
                    checked={catcherCfg.publish}
                    onChange={(e) => setCatcherCfg((p) => ({ ...p, publish: e.target.checked }))}
                    className="accent-fuchsia-500"
                  />
                  <span className="font-bold text-white/80">Auto-publish (else drafts)</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    data-testid="news-admin-catcher-heroes"
                    checked={catcherCfg.generate_heroes}
                    onChange={(e) => setCatcherCfg((p) => ({ ...p, generate_heroes: e.target.checked }))}
                    className="accent-fuchsia-500"
                  />
                  <span className="font-bold text-white/80">Generate AI hero per story</span>
                </label>
              </div>
            </div>
          </div>

          {/* Smart Topic Buckets — one-tap presets */}
          <div className="rounded-lg bg-black/30 ring-1 ring-fuchsia-500/20 p-3" data-testid="news-admin-catcher-buckets">
            <div className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-300 font-black mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Smart Topic Buckets — 1-tap presets
            </div>
            <div className="flex flex-wrap gap-1.5">
              {TOPIC_BUCKETS.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  data-testid={`news-admin-catcher-bucket-${b.id}`}
                  onClick={() => applyBucket(b)}
                  className="px-3 py-1.5 rounded-full text-xs font-bold bg-white/5 hover:bg-fuchsia-500/20 ring-1 ring-white/10 hover:ring-fuchsia-500/40 transition flex items-center gap-1.5"
                  title={`Sources: ${b.sources.join(", ")}\nKeywords: ${b.keywords.join(", ")}`}
                >
                  <span>{b.emoji}</span> {b.label}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-white/40 mt-2">
              Tap a bucket to auto-load its sources & keywords. You can fine-tune below before catching.
            </div>
          </div>

          {/* Keyword filters */}
          <div className="grid gap-3 sm:grid-cols-2 rounded-lg bg-black/30 ring-1 ring-white/10 p-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold block mb-1">
                🎯 Find these keywords (comma-separated, optional)
              </label>
              <input
                data-testid="news-admin-catcher-keywords"
                type="text"
                value={catcherKwInclude}
                onChange={(e) => setCatcherKwInclude(e.target.value)}
                placeholder="lebron, kendrick, fashion week, fall recipes, AI startup"
                className="w-full px-3 py-2 bg-black/40 ring-1 ring-emerald-500/30 rounded-lg text-white text-sm focus:ring-emerald-400 outline-none"
              />
              <div className="text-[10px] text-white/40 mt-1">Include only stories whose title or summary contains ANY of these.</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-red-300 font-bold block mb-1">
                🚫 Block these keywords (optional)
              </label>
              <input
                data-testid="news-admin-catcher-block-keywords"
                type="text"
                value={catcherKwBlock}
                onChange={(e) => setCatcherKwBlock(e.target.value)}
                placeholder="obituary, tragic, accident, shooting"
                className="w-full px-3 py-2 bg-black/40 ring-1 ring-red-500/30 rounded-lg text-white text-sm focus:ring-red-400 outline-none"
              />
              <div className="text-[10px] text-white/40 mt-1">Skip stories matching ANY of these (great for tone control).</div>
            </div>
          </div>

          {/* Source Trust grid — grouped by category */}
          <div data-testid="news-admin-catcher-source-grid" className="rounded-lg bg-black/30 ring-1 ring-white/10 p-3 space-y-3 max-h-80 overflow-y-auto">
            <div className="flex items-center justify-between sticky top-0 bg-black/80 backdrop-blur-sm -mx-3 -mt-3 px-3 py-2 border-b border-white/10 z-10">
              <div className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-300 font-black flex items-center gap-1.5">
                <TvIcon className="w-3.5 h-3.5" /> Source Trust ({catcherSources.length} outlets)
              </div>
              <button
                type="button"
                onClick={handleSaveCatcherSettings}
                disabled={savingSettings}
                data-testid="news-admin-catcher-save-settings"
                className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 px-2.5 py-1 rounded ring-1 ring-emerald-500/30 disabled:opacity-50"
              >
                {savingSettings ? "Saving…" : "Save trust settings"}
              </button>
            </div>
            {Object.entries(catcherSources.reduce((acc, s) => {
              (acc[s.category] = acc[s.category] || []).push(s); return acc;
            }, {})).map(([cat, list]) => (
              <div key={cat} data-testid={`news-admin-catcher-cat-${cat}`}>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-1.5">{cat} ({list.length})</div>
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {list.map((s) => {
                    const w = catcherWeights[s.id] ?? 1;
                    const enabled = w > 0;
                    return (
                      <div
                        key={s.id}
                        data-testid={`news-admin-catcher-row-${s.id}`}
                        className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ring-1 ${enabled ? "bg-fuchsia-500/5 ring-fuchsia-500/20" : "bg-black/30 ring-white/5"}`}
                      >
                        <button
                          type="button"
                          onClick={() => setCatcherWeights((p) => ({ ...p, [s.id]: enabled ? 0 : 1 }))}
                          data-testid={`news-admin-catcher-toggle-${s.id}`}
                          className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-black shrink-0 ${enabled ? "bg-fuchsia-500 text-black" : "bg-white/10 text-white/50"}`}
                          title={enabled ? "Click to disable" : "Click to enable"}
                        >
                          {enabled ? "ON" : "off"}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold truncate">{s.label}</div>
                          <input
                            type="range"
                            min={0} max={3} step={0.1}
                            value={w}
                            onChange={(e) => setCatcherWeights((p) => ({ ...p, [s.id]: parseFloat(e.target.value) }))}
                            data-testid={`news-admin-catcher-weight-${s.id}`}
                            disabled={!enabled}
                            className="w-full h-1 mt-0.5 accent-fuchsia-500 disabled:opacity-30"
                          />
                        </div>
                        <span className="text-[10px] font-mono font-bold text-fuchsia-300 w-7 text-right shrink-0">{w.toFixed(1)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            disabled={catcherBusy || Object.values(catcherWeights).every((w) => !w || w <= 0)}
            onClick={handleRunCatcher}
            data-testid="news-admin-catcher-run"
            className="bg-fuchsia-500 hover:bg-fuchsia-400 text-black font-black uppercase tracking-wider text-sm px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Sparkles className="w-4 h-4" /> {catcherBusy ? "Catching headlines…" : "Catch top stories now"}
          </button>

          {catcherResult && (
            <div data-testid="news-admin-catcher-result" className="rounded-lg bg-black/40 ring-1 ring-white/10 p-3 text-xs">
              <div className="text-fuchsia-200 font-bold mb-2 flex items-center gap-3 flex-wrap">
                <span>Caught {catcherResult.count} stor{catcherResult.count === 1 ? "y" : "ies"}</span>
                {typeof catcherResult.candidates_seen === "number" && (
                  <span className="text-white/50 font-normal">
                    · scanned {catcherResult.candidates_seen} headlines · {catcherResult.matched_after_filters} matched filters
                  </span>
                )}
                <span className="text-white/50 font-normal">{catcherResult.rewritten ? "· rewritten in Tap In voice" : "· raw RSS"}</span>
              </div>
              <ul className="space-y-1">
                {(catcherResult.created || []).map((c) => (
                  <li key={c.id} className="text-white/75 flex items-center gap-2">
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 uppercase tracking-wider font-bold shrink-0">{c.source_label || c.source}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-fuchsia-500/15 text-fuchsia-300 uppercase tracking-wider font-bold shrink-0">{c.status}</span>
                    <span className="font-bold truncate flex-1">{c.title}</span>
                    <button
                      onClick={() => api.get(`/news/${c.slug}`).then((r) => { setEditing(r.data); setTagsInput((r.data.tags || []).join(", ")); setShowCatcher(false); }).catch(() => {})}
                      className="text-tv-accent hover:underline text-[10px] font-bold uppercase tracking-wider shrink-0"
                    >
                      Edit
                    </button>
                  </li>
                ))}
              </ul>
              {(catcherResult.skipped || []).length > 0 && (
                <div className="text-white/40 mt-2">{catcherResult.skipped.length} skipped</div>
              )}
            </div>
          )}
        </div>
      )}

      {!editing && (
        <div className="rounded-xl bg-white/5 ring-1 ring-white/10 overflow-hidden" data-testid="news-admin-list">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-white/60 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Headline</th>
                <th className="text-left px-4 py-2.5">Category</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Views</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-white/50">Loading…</td></tr>
              )}
              {!loading && posts.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-12 text-center text-white/50">
                  No stories yet — tap "New post" to publish your first headline.
                </td></tr>
              )}
              {posts.map((p) => (
                <tr key={p.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`news-admin-row-${p.slug}`}>
                  <td className="px-4 py-3">
                    <div className="font-bold">{p.title}</div>
                    <div className="text-[11px] text-white/40 uppercase tracking-wider mt-0.5">/tap-in-news/{p.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-white/80">{p.category}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${p.status === "published" ? "bg-emerald-500/20 text-emerald-300" : "bg-yellow-500/15 text-yellow-300"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white/60">{(p.view_count || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => startEdit(p)}
                      data-testid={`news-admin-edit-${p.slug}`}
                      className="text-tv-accent hover:underline text-xs font-bold mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      data-testid={`news-admin-delete-${p.slug}`}
                      className="text-red-400 hover:underline text-xs font-bold"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-4 sm:p-6 space-y-4" data-testid="news-admin-editor">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-lg font-display font-bold">
                {editing.id ? "Edit story" : "New story"}
              </h3>
              <div className="text-xs text-white/50 mt-0.5">
                {editing.id ? `Status: ${editing.status} · /tap-in-news/${editing.slug}` : "Draft — won't be public until you publish"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPreview((p) => !p)}
                data-testid="news-admin-toggle-preview"
                className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-bold flex items-center gap-1.5"
              >
                <Eye className="w-3.5 h-3.5" /> {showPreview ? "Edit" : "Preview"}
              </button>
              <button
                onClick={cancelEdit}
                data-testid="news-admin-cancel"
                className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-bold flex items-center gap-1.5"
              >
                <X className="w-3.5 h-3.5" /> Close
              </button>
            </div>
          </div>

          {!showPreview && (
            <>
              {/* Headline + dek */}
              <div className="space-y-3">
                <input
                  data-testid="news-admin-title"
                  type="text"
                  placeholder="Headline (e.g. 'KENDRICK DROPS SURPRISE ALBUM AT 3AM')"
                  value={editing.title}
                  onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))}
                  className="w-full px-4 py-3 bg-black/40 ring-1 ring-white/15 rounded-lg text-white text-xl font-display font-black placeholder:text-white/30 focus:ring-tv-accent outline-none"
                />
                <textarea
                  data-testid="news-admin-dek"
                  placeholder="Dek (sub-headline, 1-2 lines under the title)"
                  value={editing.dek}
                  onChange={(e) => setEditing((p) => ({ ...p, dek: e.target.value }))}
                  rows={2}
                  className="w-full px-4 py-2.5 bg-black/40 ring-1 ring-white/15 rounded-lg text-white placeholder:text-white/30 focus:ring-tv-accent outline-none resize-none"
                />
              </div>

              {/* Meta row */}
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold block mb-1">Category</label>
                  <select
                    data-testid="news-admin-category"
                    value={editing.category}
                    onChange={(e) => setEditing((p) => ({ ...p, category: e.target.value }))}
                    className="w-full px-3 py-2 bg-black/40 ring-1 ring-white/15 rounded-lg text-white text-sm focus:ring-tv-accent outline-none"
                  >
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold block mb-1">Author</label>
                  <input
                    data-testid="news-admin-author"
                    type="text"
                    placeholder="Tap In Staff"
                    value={editing.author_name}
                    onChange={(e) => setEditing((p) => ({ ...p, author_name: e.target.value }))}
                    className="w-full px-3 py-2 bg-black/40 ring-1 ring-white/15 rounded-lg text-white text-sm focus:ring-tv-accent outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold block mb-1">Tags (comma-separated)</label>
                  <input
                    data-testid="news-admin-tags"
                    type="text"
                    placeholder="hip-hop, exclusive, weekend"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    className="w-full px-3 py-2 bg-black/40 ring-1 ring-white/15 rounded-lg text-white text-sm focus:ring-tv-accent outline-none"
                  />
                </div>
              </div>

              {/* Hero image */}
              <div className="rounded-lg bg-black/30 ring-1 ring-white/10 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold mb-3 flex items-center gap-1.5">
                  <ImageIcon className="w-3.5 h-3.5" /> Hero image (1200×630 recommended)
                </div>
                {editing.hero_image_url && (
                  <img src={editing.hero_image_url} alt="" className="w-full max-h-64 object-cover rounded-lg mb-3" data-testid="news-admin-hero-preview" />
                )}
                <div className="flex flex-wrap gap-2">
                  <label className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-xs font-bold cursor-pointer flex items-center gap-1.5" data-testid="news-admin-hero-upload">
                    <ImageIcon className="w-3.5 h-3.5" /> Upload hero
                    <input type="file" accept="image/*" hidden onChange={(e) => handleHeroFile(e.target.files?.[0])} />
                  </label>
                  <button
                    onClick={handleAiHero}
                    disabled={busy || !editing.id}
                    data-testid="news-admin-ai-hero"
                    title={editing.id ? "Generate using Gemini Nano Banana" : "Save the draft first"}
                    className="px-3 py-2 rounded-lg bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 text-xs font-bold flex items-center gap-1.5 disabled:opacity-40"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> AI generate from headline
                  </button>
                  {editing.hero_image_url && (
                    <button
                      onClick={() => setEditing((p) => ({ ...p, hero_image_url: "" }))}
                      data-testid="news-admin-hero-clear"
                      className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-bold flex items-center gap-1.5 text-white/60"
                    >
                      <X className="w-3.5 h-3.5" /> Remove
                    </button>
                  )}
                </div>
                <div className="text-xs text-white/40 mt-2">
                  No hero? Click "AI generate" after saving — we'll create one from your headline. The TMZ-style share card auto-renders on publish.
                </div>
              </div>

              {/* Body markdown */}
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold block mb-1">
                  Story body (Markdown — # heading, **bold**, *italic*, [link](url), &gt; quote, - bullets)
                </label>
                <textarea
                  data-testid="news-admin-body"
                  value={editing.body_md}
                  onChange={(e) => setEditing((p) => ({ ...p, body_md: e.target.value }))}
                  rows={14}
                  placeholder="Write your story here..."
                  className="w-full px-4 py-3 bg-black/40 ring-1 ring-white/15 rounded-lg text-white text-sm font-mono leading-relaxed focus:ring-tv-accent outline-none resize-y"
                />
              </div>
            </>
          )}

          {showPreview && (
            <div className="rounded-lg bg-black/40 ring-1 ring-white/10 p-4 sm:p-6" data-testid="news-admin-preview">
              <div className="text-[10px] uppercase tracking-[0.2em] text-tv-accent font-bold mb-2">Preview</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-tight mb-3">
                <span className="bg-tv-accent text-black px-1 box-decoration-clone">{editing.title || "Untitled"}</span>
              </h2>
              {editing.dek && <p className="text-lg text-white/80 mb-4">{editing.dek}</p>}
              {editing.hero_image_url && <img src={editing.hero_image_url} alt="" className="w-full rounded-xl mb-4" />}
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
            <button
              onClick={() => handleSave({ publish: false })}
              disabled={busy}
              data-testid="news-admin-save-draft"
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm font-bold flex items-center gap-1.5 disabled:opacity-40"
            >
              <Save className="w-4 h-4" /> Save draft
            </button>
            <button
              onClick={() => handleSave({ publish: true })}
              disabled={busy}
              data-testid="news-admin-publish"
              className="px-4 py-2 rounded-lg bg-tv-accent text-black text-sm font-bold flex items-center gap-1.5 disabled:opacity-40"
            >
              <Send className="w-4 h-4" /> {editing.status === "published" ? "Save & re-publish" : "Publish now"}
            </button>
            {editing.id && editing.status === "published" && (
              <button
                onClick={handlePublishToggle}
                disabled={busy}
                data-testid="news-admin-unpublish"
                className="px-4 py-2 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-200 text-sm font-bold flex items-center gap-1.5 disabled:opacity-40"
              >
                Unpublish (move to draft)
              </button>
            )}
            {editing.id && (
              <button
                onClick={handleRegenOg}
                disabled={busy}
                data-testid="news-admin-regen-og"
                className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-bold flex items-center gap-1.5 disabled:opacity-40"
              >
                <RefreshCw className="w-4 h-4" /> Regenerate share card
              </button>
            )}
            {editing.id && (
              <button
                onClick={() => handleDelete(editing)}
                data-testid="news-admin-delete-current"
                className="ml-auto px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-xs font-bold flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            )}
          </div>

          {editing.og_image_url && (
            <div className="text-xs text-white/40 pt-2 border-t border-white/5">
              Share card: <a href={editing.og_image_url} target="_blank" rel="noreferrer" className="text-tv-accent hover:underline">{editing.og_image_url}</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
